import { useTranslation } from 'next-i18next';
import { BreadcrumbJsonLd } from 'next-seo';
import { useLocale } from '@hooks/useLocale';
import {
  LOCALE_ONLY_PATH_PATTERN,
  withLocalePrefix,
} from '@utils/breadcrumbs/withLocalePrefix';
import type { BreadcrumbItem } from './index';

export type BreadcrumbsJsonLdProps = {
  items: BreadcrumbItem[];
};

// Hardcoded rather than read from `process.env.BASE_URL`: that var is server
// only, so a client render would fall back to a different value and break
// hydration. Matches the legacy `components/algolia/Breadcrumbs` base url.
const SITE_URL = 'https://www.mec.ca';

/**
 * Emits `BreadcrumbList` structured data for the redesigned
 * `_components/Breadcrumbs`, which renders no JSON-LD of its own. Mirrors the
 * legacy `components/algolia/Breadcrumbs` output: a leading Home entry and
 * 1-based positions.
 *
 * `items` is the same array passed to `<Breadcrumbs />` (without Home, which is
 * prepended here exactly as the component prepends it visually). Items with a
 * `null` href have no canonical url to point at, so they are omitted — schema.org
 * requires an `item` on every `ListItem`.
 */
const BreadcrumbsJsonLd = ({ items }: BreadcrumbsJsonLdProps) => {
  const { t } = useTranslation('common');
  const locale = useLocale();

  const homeUrl = `${SITE_URL}/${locale}`;
  const firstItemHref = items?.[0]?.href ?? '';
  const firstItemIsHome =
    firstItemHref === '/' || LOCALE_ONLY_PATH_PATTERN.test(firstItemHref);
  const trail = (firstItemIsHome ? items.slice(1) : items ?? []).filter(
    (item): item is BreadcrumbItem & { href: string } => Boolean(item.href)
  );

  const itemListElements = [
    { position: 1, name: t('breadcrumb.home'), item: homeUrl },
    ...trail.map((item, index) => ({
      position: index + 2,
      name: item.label,
      item: `${SITE_URL}${withLocalePrefix(item.href, locale)}`,
    })),
  ];

  return <BreadcrumbJsonLd itemListElements={itemListElements} />;
};

export default BreadcrumbsJsonLd;
